import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import "../css/AminiiProductList.css";
import AminiiNavbar from "../components/AminiiNavbar";
import AminiiFooter from "../components/AminiiFooter";
import FreeShipping from "../components/FreeShipping";
import { getCookie, decodeJwtPayload } from "../Utils/authHelpers";
import { API_BASE_URL } from "../config/constants";
import { getOptimizedImageUrl } from "../Utils/upload";
import { getGuestCartCount } from "../Utils/guestCart";

function AminiiProductList() {
  const { category, subcategory } = useParams();
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [authError, setAuthError] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userFirstName, setUserFirstName] = useState("");
  const [loading, setLoading] = useState(true);
  const [cartItemCount, setCartItemCount] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();

  const fetchUserInfo = async (userId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/users/${userId}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true,
        }
      );
      const userData = response.data?.data || response.data;
      setUserFirstName(userData.firstName || "");
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleLogoutCleanup();
      }
      // Other errors: keep user logged in, just skip the name
    }
  };

  const fetchCartItemCount = async (userId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/cart/user/${userId}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true,
        }
      );
      const cartData = response.data?.data || response.data;
      setCartItemCount(Array.isArray(cartData) ? cartData.length : 0);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleLogoutCleanup();
      }
    }
  };

  useEffect(() => {
    const jwtCookie = getCookie("jwt");
    let token = jwtCookie;
    let jwtSource = "cookie";

    if (jwtCookie) {
      localStorage.setItem("token", jwtCookie);
    } else {
      token = localStorage.getItem("token");
      jwtSource = "sessionStorage";
    }

    if (token) {
      const payload = decodeJwtPayload(token);
      if (payload?.userId) {
        setIsLoggedIn(true);
        setUserFirstName(payload.firstName || "");
        fetchUserInfo(payload.userId);
        fetchCartItemCount(payload.userId);
      } else {
        setIsLoggedIn(false);
        localStorage.removeItem("token");
      }
    } else {
      setIsLoggedIn(false);
      localStorage.removeItem("token");
      setCartItemCount(getGuestCartCount());
    }

    const onGuestCartUpdate = () => {
      if (!localStorage.getItem("token")) {
        setCartItemCount(getGuestCartCount());
      }
    };
    window.addEventListener("aminii-guest-cart-updated", onGuestCartUpdate);

    return () => {
      window.removeEventListener("aminii-guest-cart-updated", onGuestCartUpdate);
    };
  }, []);

  const categoryNames = {
    "basics": "Basics",
    "best-seller": "Best seller",
    "aminii-luxe": "Aminii luxe",
    "aminii-plus": "PLUS SIZES",
    "plus": "PLUS SIZES",
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const getFirstImage = (product, width = 800) => {
    const image =
      product?.ProductImages?.[0] || product?.productImages?.[0] || "";
    if (!image) return "https://via.placeholder.com/50?text=No+Image";
    return getOptimizedImageUrl(image, { width, quality: "auto", colorSpace: "srgb" });
  };

  const formatPrice = (product) => {
    const productPrice = product.ProductPrice || product.productPrice;
    const beforeDiscountPrice = product.beforeDiscountPrice;

    if (!productPrice && !beforeDiscountPrice) {
      return {
        formattedPrice: "Price Not Available",
        formattedBeforeDiscountPrice: null,
        discountPercent: null,
        diffAmount: null,
      };
    }

    const price = productPrice ? parseFloat(productPrice) : null;
    const beforePrice = beforeDiscountPrice ? parseFloat(beforeDiscountPrice) : null;

    const formattedPrice = price ? `₹${price.toLocaleString("en-IN")}` : null;
    const formattedBeforeDiscountPrice = beforePrice
      ? `₹${beforePrice.toLocaleString("en-IN")}`
      : null;

    let discountPercent = null;
    let diffAmount = null;
    if (price && beforePrice && beforePrice > price) {
      const pct = Math.round(((beforePrice - price) / beforePrice) * 100);
      const diff = Math.round(beforePrice - price);
      discountPercent = `${pct}% Off`;
      diffAmount = `Save ₹${diff.toLocaleString("en-IN")}`;
    }

    return { formattedPrice, formattedBeforeDiscountPrice, discountPercent, diffAmount };
  };


  const handleLogoutCleanup = () => {
    localStorage.removeItem("token");
    setIsLoggedIn(false);
    setUserFirstName("");
    setCartItemCount(0);
    navigate("/");
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setAuthError("");

      const response = await axios.get(`${API_BASE_URL}/products`, {
        withCredentials: true,
      });

      const isMainImg = (cat) =>
        cat?.categoryName?.toLowerCase().replace(/[-_\s]+/g, "") === "mainimage" ||
        cat?.subCategory?.categoryName?.toLowerCase().replace(/[-_\s]+/g, "") === "mainimage";

      const rawProducts =
        response.data?.data ||
        (Array.isArray(response.data) ? response.data : []);

      const allFetched = rawProducts
        .filter((product) => {
          if (!product.productCategory) return true;
          return !product.productCategory.some(isMainImg);
        })
        .map((product) => {
          const formattedPrices = formatPrice(product);
          return {
            ...product,
            displayImage: getFirstImage(product),
            formattedPrice: formattedPrices.formattedPrice,
            formattedBeforeDiscountPrice:
              formattedPrices.formattedBeforeDiscountPrice,
          };
        });

      let filtered = allFetched;

      // Filter by Category and/or Subcategory
      if (category && category !== "all" && category !== "by") {
        const catClean = category.toLowerCase().replace(/[-_\s]+/g, "");
        const subClean = subcategory
          ? subcategory.toLowerCase().replace(/[-_\s]+/g, "")
          : null;

        const isPlusCategory =
          catClean === "aminiiplus" ||
          catClean === "plus" ||
          catClean === "plussizes" ||
          catClean === "plussize";

        filtered = allFetched.filter((p) => {
          // 1. Special check for Plus Sizes category
          if (isPlusCategory) {
            const hasCatPlus = p.productCategory?.some((c) => {
              const cName = (c.categoryName || "").toLowerCase();
              const scName = (c.subCategory?.categoryName || "").toLowerCase();
              return cName.includes("plus") || scName.includes("plus");
            });
            if (hasCatPlus) return true;

            const pTitle = (p.productName || "").toLowerCase();
            const pDesc = (p.description || "").toLowerCase();
            const pTags = (p.tags || []).map((t) => (t || "").toString().toLowerCase());
            const sizes = (p.productSize || p.ProductSize || []).map((s) =>
              (s.sizeName || s.size || s || "").toString().toLowerCase()
            );

            if (
              pTitle.includes("plus") ||
              pTitle.includes("4xl") ||
              pTitle.includes("5xl") ||
              pTitle.includes("6xl") ||
              pDesc.includes("plus") ||
              pDesc.includes("4xl") ||
              pDesc.includes("5xl") ||
              pDesc.includes("6xl") ||
              pTags.some((t) => t.includes("plus") || t.includes("4xl") || t.includes("5xl") || t.includes("6xl")) ||
              sizes.some((s) => s.includes("4xl") || s.includes("5xl") || s.includes("6xl") || s.includes("plus"))
            ) {
              return true;
            }
            return false;
          }

          const hasCatMatch = p.productCategory?.some((c) => {
            const cName = (c.categoryName || "")
              .toLowerCase()
              .replace(/[-_\s]+/g, "");
            const scName = (c.subCategory?.categoryName || "")
              .toLowerCase()
              .replace(/[-_\s]+/g, "");

            if (subClean) {
              const matchesCat =
                cName.includes(catClean) || catClean.includes(cName);
              const matchesSub =
                scName.includes(subClean) ||
                subClean.includes(scName) ||
                cName.includes(subClean);
              return matchesCat || matchesSub;
            }

            return (
              cName.includes(catClean) ||
              catClean.includes(cName) ||
              scName.includes(catClean)
            );
          });

          if (hasCatMatch) return true;

          // Fallback check against tags, title, description
          const pTitle = (p.productName || "")
            .toLowerCase()
            .replace(/[-_\s]+/g, "");
          const pDesc = (p.description || "").toLowerCase();
          const pTags = (p.tags || []).map((t) =>
            t.toLowerCase().replace(/[-_\s]+/g, "")
          );

          if (pTags.some((t) => t.includes(catClean) || catClean.includes(t)))
            return true;
          if (pTitle.includes(catClean)) return true;
          if (subClean && (pTitle.includes(subClean) || pDesc.includes(subClean)))
            return true;

          return false;
        });
      }

      // Handle search query if present
      if (location.state?.searchQuery) {
        const query = location.state.searchQuery.toLowerCase().trim();
        filtered = filtered.filter((p) => {
          const pName = (p.productName || "").toLowerCase();
          const pDesc = (p.description || "").toLowerCase();
          return pName.includes(query) || pDesc.includes(query);
        });
      }

      // Sort newest first (highest productId = most recently added)
      filtered.sort((a, b) => (b.productId || 0) - (a.productId || 0));

      setAllProducts(filtered);
      setProducts(filtered);

      if (filtered.length === 0 && category && category !== "all" && category !== "by") {
        const catLabel =
          categoryNames[category] || category.replace(/-/g, " ");
        const subLabel = subcategory
          ? ` - ${subcategory.replace(/-/g, " ")}`
          : "";
        setAuthError(`No products found for ${catLabel}${subLabel}`);
      } else {
        setAuthError("");
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        setAuthError("Session expired. Please log in again.");
        handleLogoutCleanup();
      } else {
        setAuthError("Failed to fetch products. Please try again later.");
      }
      setProducts([]);
      setAllProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [category, subcategory, location.state?.searchQuery]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target;
            img.src = img.dataset.src;
            img.classList.add("amin-image-loaded");
            observer.unobserve(img);
          }
        });
      },
      { rootMargin: "50px", threshold: 0 }
    );

    const lazyImages = document.querySelectorAll(".amin-lazy-load");
    lazyImages.forEach((img) => observer.observe(img));

    return () => {
      lazyImages.forEach((img) => observer.unobserve(img));
    };
  }, [products]);

  const SkeletonProduct = () => (
    <div className="pl-product skeleton">
      <div className="pl-productImage skeleton-box" />
      <div className="skeleton-line" style={{ width: "80%" }} />
      <div className="skeleton-line" style={{ width: "60%" }} />
    </div>
  );

  const handleImageError = (e) => {
    e.target.classList.add("amin-image-error");
    if (e.target.nextSibling && e.target.nextSibling.style) {
      e.target.nextSibling.style.display = "flex";
    }
    e.target.src =
      "https://placehold.co/600x800/f5f5f5/888888?text=Image+Unavailable";
    e.target.onerror = null;
  };

  const AVAILABLE_SIZES = ["S", "M", "L", "XL", "XXL", "3XL", "4XL", "5XL", "6XL"];
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [sortBy, setSortBy] = useState("featured");

  const toggleSizeFilter = (size) => {
    setSelectedSizes((prev) =>
      prev.includes(size) ? prev.filter((s) => s !== size) : [...prev, size]
    );
  };

  const clearAllFilters = () => {
    setSelectedSizes([]);
    setSortBy("featured");
  };

  // Compute filtered & sorted products
  const getProcessedProducts = () => {
    let result = [...allProducts];

    // Filter by selected sizes if any
    if (selectedSizes.length > 0) {
      result = result.filter((product) => {
        const prodSizes = product.productSize || product.ProductSize || [];
        if (!Array.isArray(prodSizes)) return true;
        return prodSizes.some((s) => {
          const sName = (s.sizeName || s).toString().toUpperCase();
          return selectedSizes.includes(sName);
        });
      });
    }

    // Apply sorting
    if (sortBy === "price-low") {
      result.sort((a, b) => (parseFloat(a.productPrice || 0) - parseFloat(b.productPrice || 0)));
    } else if (sortBy === "price-high") {
      result.sort((a, b) => (parseFloat(b.productPrice || 0) - parseFloat(a.productPrice || 0)));
    } else if (sortBy === "newest") {
      result.sort((a, b) => (new Date(b.productDate || 0) - new Date(a.productDate || 0)));
    }

    return result;
  };

  const displayedProducts = getProcessedProducts();

  return (
    <div className="amin-dashboard">
      <AminiiNavbar
        isLoggedIn={isLoggedIn}
        userFirstName={userFirstName}
        cartItemCount={cartItemCount}
        onLogout={handleLogoutCleanup}
      />

      {authError && (
        <div className="amin-error-state">
          <p className="amin-error-icon">&#9888;</p>
          <p className="amin-error-title">Unable to load products</p>
          <p className="amin-error-subtitle">Please check your connection and try again.</p>
          <button className="amin-error-retry" onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      )}

      {/* Filter & Sort Toolbar */}
      <div className="pl-toolbar">
        <button
          className="pl-filter-btn"
          onClick={() => setIsFilterOpen(true)}
          aria-label="Open filters"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="4" y1="21" x2="4" y2="14" />
            <line x1="4" y1="10" x2="4" y2="3" />
            <line x1="12" y1="21" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12" y2="3" />
            <line x1="20" y1="21" x2="20" y2="16" />
            <line x1="20" y1="12" x2="20" y2="3" />
            <line x1="1" y1="14" x2="7" y2="14" />
            <line x1="9" y1="8" x2="15" y2="8" />
            <line x1="17" y1="16" x2="23" y2="16" />
          </svg>
          <span>FILTER</span>
          {selectedSizes.length > 0 && (
            <span className="pl-filter-badge">{selectedSizes.length}</span>
          )}
        </button>

        <div className="pl-sort-container">
          <label htmlFor="pl-sort-select">SORT BY:</label>
          <select
            id="pl-sort-select"
            className="pl-sort-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="featured">Featured</option>
            <option value="price-low">Price: Low to High</option>
            <option value="price-high">Price: High to Low</option>
            <option value="newest">Newest</option>
          </select>
        </div>
      </div>

      {/* Filter Drawer Backdrop */}
      {isFilterOpen && (
        <div
          className="pl-filter-overlay"
          onClick={() => setIsFilterOpen(false)}
        />
      )}

      {/* Slide-in Filter Drawer */}
      <aside className={`pl-filter-drawer ${isFilterOpen ? "open" : ""}`}>
        <div className="pl-drawer-header">
          <h3>FILTER PRODUCTS</h3>
          <button
            className="pl-drawer-close"
            onClick={() => setIsFilterOpen(false)}
            aria-label="Close filters"
          >
            ✕
          </button>
        </div>

        <div className="pl-drawer-content">
          <div className="pl-filter-group">
            <h4 className="pl-group-title">SIZE</h4>
            <div className="pl-size-checkboxes">
              {AVAILABLE_SIZES.map((size) => {
                const count = allProducts.filter((p) => {
                  const pSizes = p.productSize || p.ProductSize || [];
                  return Array.isArray(pSizes) && pSizes.some((s) => (s.sizeName || s).toString().toUpperCase() === size);
                }).length;

                return (
                  <label key={size} className={`pl-size-chip ${selectedSizes.includes(size) ? "active" : ""}`}>
                    <input
                      type="checkbox"
                      checked={selectedSizes.includes(size)}
                      onChange={() => toggleSizeFilter(size)}
                    />
                    <span>{size}</span>
                    {count > 0 && <span className="pl-chip-count">({count})</span>}
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        <div className="pl-drawer-footer">
          <button className="pl-btn-reset" onClick={clearAllFilters}>
            Clear All
          </button>
          <button className="pl-btn-apply" onClick={() => setIsFilterOpen(false)}>
            Apply Filters ({displayedProducts.length})
          </button>
        </div>
      </aside>

      <main className="pl-productPage">
        {loading
          ? Array(8)
              .fill()
              .map((_, i) => <SkeletonProduct key={i} />)
          : displayedProducts.length === 0 ? (
              <div className="pl-no-results">
                <p>No products match your selected filters.</p>
                <button className="amin-explore-btn" onClick={clearAllFilters}>
                  Reset Filters
                </button>
              </div>
            )
          : displayedProducts.map((product) => {
              const pSizes = product.productSize || product.ProductSize;
              const isProdSoldOut =
                Array.isArray(pSizes) && pSizes.length > 0
                  ? pSizes.every((s) => {
                      if (!s) return true;
                      if (s.isSoldOut || s.soldOut || s.status === "sold" || s.status === "SOLD_OUT") return true;
                      const rawStock = s.stock ?? s.quantity;
                      if (rawStock === undefined || rawStock === null || rawStock === "") return true;
                      if (typeof rawStock === "string" && rawStock.toLowerCase().includes("sold")) return true;
                      const numStock = Number(rawStock);
                      return isNaN(numStock) || numStock <= 0;
                    })
                  : Array.isArray(pSizes) && pSizes.length === 0
                  ? (product.stock !== undefined && product.stock !== null ? Number(product.stock) <= 0 : true)
                  : product.stock !== undefined && product.stock !== null
                  ? Number(product.stock) <= 0
                  : false;

              return (
                <div
                  key={product.productId}
                  className={`pl-product ${isProdSoldOut ? "is-sold-out" : ""}`}
                  onClick={() => navigate(`/productDetails/${product.productId}`)}
                >
                  <div className="amin-image-container">
                    {isProdSoldOut && (
                      <span className="pl-sold-out-badge">Sold Out</span>
                    )}
                    <img
                      className="pl-productImage"
                      loading="lazy"
                      decoding="async"
                      src={
                        product.displayImage ||
                        getFirstImage(product) ||
                        "https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?auto=format&fit=crop&w=600&q=80"
                      }
                      alt={product.productName || "Product image"}
                      onError={handleImageError}
                    />
                  </div>
                  <div className="product-info">
                  <p className="product-name">
                    {product.productName || "Unnamed Product"}
                  </p>
                  <div className="product-price-container">
                    <span className="product-price">
                      {product.formattedPrice || "Price Not Available"}
                    </span>
                    {product.formattedBeforeDiscountPrice && (
                      <s className="product-before-discount-price">
                        {product.formattedBeforeDiscountPrice}
                      </s>
                    )}
                    {product.discountPercent && (
                      <span className="product-discount-badge">{product.discountPercent}</span>
                    )}
                  </div>
                  {product.diffAmount && (
                    <div className="product-save-amount">{product.diffAmount}</div>
                  )}
                </div>

              </div>
            );
          })}
      </main>

      <FreeShipping />
      <AminiiFooter />
    </div>
  );
}

export default AminiiProductList;
